import React from 'react';

import { generateActionPlanMarkdown } from '~/components/Claim/Insights/ClaimActionPlan/ClaimActionPlanProcessor';
import { Insight } from '~/components/Claim/Insights/types';
import ComplexityIcon from '~/core/iconComponents/icons/insights/ComplexityIcon.tsx';
import DataExtractionIcon from '~/core/iconComponents/icons/insights/DataExtractionIcon.tsx';
import DataPointIcon from '~/core/iconComponents/icons/insights/DataPointIcon.tsx';
import FinancialIcon from '~/core/iconComponents/icons/insights/FinancialIcon.tsx';
import GeneralIcon from '~/core/iconComponents/icons/insights/GeneralIcon.tsx';
import WarningIcon from '~/core/iconComponents/icons/insights/WarningIcon.tsx';
import type { IconProps } from '~/core/iconComponents/types';
import { parseDateWithOptionalTimezone, toTimeAgo } from '~/utils/Dates/DateTimeUtils';

export const INSIGHT_ICON_MAP: Record<string, React.ComponentType<IconProps>> = {
  warning: WarningIcon,
  data_point: DataPointIcon,
  data_extraction: DataExtractionIcon,
  financial: FinancialIcon,
  general: GeneralIcon,
  complexity: ComplexityIcon,
};

export const getInsightIcon = (insight: Insight): React.ComponentType<IconProps> => {
  return (insight.key && INSIGHT_ICON_MAP[insight.key]) || INSIGHT_ICON_MAP[insight.type] || GeneralIcon;
};

export const insightToListItem = (insight: Insight) => {
  const IconComponent = getInsightIcon(insight);

  const timeAgo = insight.updated_at ? toTimeAgo(parseDateWithOptionalTimezone(insight.updated_at)) : '';

  return {
    id: insight.id,
    content: (
      <div className="flex w-full items-center pr-8">
        <div>{insight.title}</div>
        <div className="ml-auto text-sm font-normal text-blue-950">{timeAgo}</div>
      </div>
    ),
    icon: (
      <div className="flex items-center justify-center rounded-lg bg-pink-100 p-4">
        <IconComponent size={16} className="text-pink-600" />
      </div>
    ),
  };
};

export const sortInsights = (insights: Insight[] = []) => {
  return [...insights].sort((a, b) => {
    if (a.created_at && b.created_at) {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }

    // Handle cases where created_at might be undefined
    if (a.created_at && !b.created_at) return -1;
    if (!a.created_at && b.created_at) return 1;

    return 0;
  });
};

export const INSIGHT_PROCESSORS_MAP: Record<string, (insight: Insight) => string> = {
  claim_action_plan: generateActionPlanMarkdown,
};
